// src/pages/talleres/editar/ResumenForm.jsx
import React from 'react';
import { Card, Typography, Divider, Space, Tag, Alert } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

const { Title, Text } = Typography;

export default function ResumenForm({ 
  data, 
  formInfo, 
  formFechas, 
  formPlanes, 
  planActive, 
  esGratuito, 
  recintos 
}) {
  const infoValues = formInfo.getFieldsValue();
  const fechasValues = formFechas.getFieldsValue();
  const planesValues = formPlanes.getFieldsValue();

  const recintoSeleccionado = recintos.find(r => r.value === infoValues.recinto);

  // 🆕 Obtener fechas con hora
  const inscripcionInicio = fechasValues.inscripcionInicio;
  const inscripcionFin = fechasValues.inscripcionFin;
  const fechasActividades = fechasValues.fechasActividades;

  const tieneFechasActividades = fechasActividades && 
    Array.isArray(fechasActividades) && 
    fechasActividades[0] && 
    fechasActividades[1];

  const diasTaller = tieneFechasActividades
    ? dayjs(fechasActividades[1]).diff(dayjs(fechasActividades[0]), 'day')
    : null;

  return (
    <Card title="📋 Resumen del taller">
      <Space direction="vertical" size="large" className="w-full">
        {/* Información básica */}
        <div>
          <Title level={5}>{infoValues.nombre}</Title>
          <Text type="secondary">{infoValues.descripcion}</Text>
        </div>

        <Divider />

        {/* Recinto y tipo */}
        <div>
          <Text strong>Recinto:</Text>
          <div><Text>{recintoSeleccionado?.label}</Text></div>
        </div>

        <div>
          <Text strong>Tipo de actividad:</Text>
          <div><Text>{infoValues.tipoActividad}</Text></div>
        </div>

        {/* Edades */}
        {(infoValues.edadMin || infoValues.edadMax) && (
          <div>
            <Text strong>Rango de edad:</Text>
            <div>
              <Text>
                {infoValues.edadMin && infoValues.edadMax
                  ? `${infoValues.edadMin} - ${infoValues.edadMax} años`
                  : infoValues.edadMin
                  ? `Desde ${infoValues.edadMin} años`
                  : `Hasta ${infoValues.edadMax} años`}
              </Text>
            </div>
          </div>
        )}

        <Divider />

        {/* 🆕 Fechas de inscripción CON HORA */}
        {inscripcionInicio && inscripcionFin && (
          <div>
            <Text strong>📝 Periodo de inscripción:</Text>
            <div className="mt-2 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">🟢</span>
                <div>
                  <Text strong>Apertura:</Text>
                  <div className="text-sm">
                    {dayjs(inscripcionInicio).format('dddd, DD [de] MMMM [de] YYYY')}
                  </div>
                  <div className="text-sm">
                    <ClockCircleOutlined /> {dayjs(inscripcionInicio).format('HH:mm')} hrs
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <span className="text-red-600 font-bold">🔴</span>
                <div>
                  <Text strong>Cierre:</Text>
                  <div className="text-sm">
                    {dayjs(inscripcionFin).format('dddd, DD [de] MMMM [de] YYYY')}
                  </div>
                  <div className="text-sm">
                    <ClockCircleOutlined /> {dayjs(inscripcionFin).format('HH:mm')} hrs
                  </div>
                </div>
              </div>

              <div className="mt-2">
                <Tag color="blue">
                  Ventana de {dayjs(inscripcionFin).diff(dayjs(inscripcionInicio), 'day')} días, {dayjs(inscripcionFin).diff(dayjs(inscripcionInicio), 'hour') % 24} horas
                </Tag>
              </div>
            </div>
          </div>
        )}

        {/* Fechas de actividades */}
        {tieneFechasActividades && (
          <>
            <Divider />
            <div>
              <Text strong>📅 Periodo de actividades:</Text>
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarOutlined />
                  <Text>
                    <strong>Inicio:</strong> {dayjs(fechasActividades[0]).format('DD/MM/YYYY')}
                  </Text>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarOutlined />
                  <Text>
                    <strong>Fin:</strong> {dayjs(fechasActividades[1]).format('DD/MM/YYYY')}
                  </Text>
                </div>
                <div className="mt-2">
                  <Tag color="purple">
                    {diasTaller} días de duración
                  </Tag>
                  {diasTaller < 30 && (
                    <Tag color="orange">
                      Taller corto
                    </Tag>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <Divider />

        {/* Tipo de taller */}
        <div>
          <Text strong>Tipo de taller:</Text>
          <div>
            <Tag color={infoValues.tipoTaller === 'libre' ? 'green' : 'blue'}>
              {infoValues.tipoTaller === 'libre' ? '🌍 Libre (acceso público)' : '🔒 Academia (acceso restringido)'}
            </Tag>
          </div>
        </div>

        {/* Precio */}
        <div>
          <Text strong>Precio:</Text>
          <div>
            <Tag color={esGratuito ? 'success' : 'warning'}>
              {esGratuito ? 'Gratuito' : 'De pago'}
            </Tag>
          </div>
        </div>

        {/* Modalidades (si no es gratuito) */}
        {!esGratuito && planesValues.valores && (
          <div>
            <Text strong>Modalidades de pago:</Text>
            <div className="mt-2 space-y-2">
              {Object.entries(planActive).map(([plan, activo]) => {
                if (activo && planesValues.valores[plan]) {
                  const config = planesValues.valores[plan];
                  return (
                    <div key={plan} className="bg-gray-50 p-3 rounded">
                      <Tag color="blue" className="mb-2">
                        {plan.charAt(0).toUpperCase() + plan.slice(1)}
                      </Tag>
                      <div className="text-sm space-y-1">
                        {config.valorParticular > 0 && (
                          <div>Particular: ${config.valorParticular.toLocaleString('es-CL')}</div>
                        )}
                        {config.valorVecino > 0 && (
                          <div>Vecino: ${config.valorVecino.toLocaleString('es-CL')}</div>
                        )}
                        {config.valorAdultoMayor > 0 && (
                          <div>Adulto Mayor: ${config.valorAdultoMayor.toLocaleString('es-CL')}</div>
                        )}
                        {config.valorFuncionarios > 0 && (
                          <div>Funcionarios: ${config.valorFuncionarios.toLocaleString('es-CL')}</div>
                        )}
                        {config.matricula > 0 && (
                          <div className="font-semibold">Matrícula: ${config.matricula.toLocaleString('es-CL')}</div>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}

        {/* Otras configuraciones */}
        <Divider />
        <div>
          <Text strong>Configuraciones adicionales:</Text>
          <div className="mt-2 flex flex-wrap gap-2">
            {infoValues.soloTarjetaVecino && (
              <Tag color="orange">Solo Tarjeta Vecino</Tag>
            )}
            {infoValues.requiereMatricula && (
              <Tag color="purple">Requiere Matrícula del Recinto</Tag>
            )}
            {infoValues.liberarCuposPorBaja && (
              <Tag color="cyan">Libera cupos por baja</Tag>
            )}
            {infoValues.tipoDuracionSuscripcion && (
              <Tag color="geekblue">
                Duración: {infoValues.tipoDuracionSuscripcion.replace('_', ' ')}
              </Tag>
            )}
          </div>
        </div>

        {/* 🆕 Advertencia sobre horarios */}
        {(inscripcionInicio && dayjs(inscripcionInicio).isBefore(dayjs().add(1, 'hour'))) && (
          <>
            <Divider />
            <Alert
              message="⚠️ Apertura próxima"
              description="Las inscripciones se abrirán pronto. Asegúrate de que el taller esté configurado correctamente antes de guardar."
              type="warning"
              showIcon
              icon={<InfoCircleOutlined />}
            />
          </>
        )}
      </Space>
    </Card>
  );
}
